import { inIframe, loadScript } from "lib/utils/helpers";

const PAYTM_PG_URL = "https://secure.paytmpayments.com/merchantpgpui/checkoutjs/merchants";

export const loadPaytmDefaultSDK = async (mid: string) => {
  try {
    const response = await loadScript(`${PAYTM_PG_URL}/${mid}.js`, async () => {
      await onPaytmDefaultSdkLoad();
    });
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const onPaytmDefaultSdkLoad = () =>
  new Promise((resolve, reject) => {
    try {
      (window as any).Paytm.CheckoutJS.onLoad(() => resolve({}));
    } catch (e) {
      throw reject(e);
    }
  });

// Paytm's SDK treats any window narrower than 1200px as a mobile web view (`innerWidth < 1200` in its device check)
// and then never enables the UPI QR. Inside the storefront iframe, checkout is narrower than that even on a big
// screen, so there Paytm mounts in the payment dialog (see CashfreePaymentForm) and is shown a desktop width while
// it is open. Restored by resetPaytmDesktopWidth once Paytm is closed.
const PAYTM_INLINE_CONTAINER_ID = "payment-form";
const PAYTM_DESKTOP_MIN_WIDTH = 1201;

export const shouldMountPaytmInline = () => inIframe() && window.screen.width >= PAYTM_DESKTOP_MIN_WIDTH;

let restoreInnerWidth: (() => void) | null = null;

const forcePaytmDesktopWidth = () => {
  if (restoreInnerWidth) return;
  const original = Object.getOwnPropertyDescriptor(window, "innerWidth");
  const readWidth = () => original?.get?.call(window) ?? document.documentElement.clientWidth;
  Object.defineProperty(window, "innerWidth", {
    configurable: true,
    get: () => Math.max(readWidth(), PAYTM_DESKTOP_MIN_WIDTH),
  });

  // Our dialogs' scroll lock (react-remove-scroll-bar) sizes its scrollbar gap as `innerWidth - clientWidth`, which the
  // desktop width inflates into a huge body margin. Cancel that margin for as long as the desktop width is in place.
  const scrollLockGuard = document.createElement("style");
  scrollLockGuard.textContent = "html body[data-scroll-locked] { margin-right: 0 !important; }";
  document.head.appendChild(scrollLockGuard);

  restoreInnerWidth = () => {
    if (original) Object.defineProperty(window, "innerWidth", original);
    else delete (window as any).innerWidth;
    scrollLockGuard.remove();
    restoreInnerWidth = null;
  };
};

export const resetPaytmDesktopWidth = () => restoreInnerWidth?.();

interface PaytmDefaultPayment {
  orderId: string;
  token: string;
  amount: number;
  onCancelHandler?: () => void;
}
export const initializePaytmDefaultPayment = async ({
  orderId,
  token,
  amount,
  onCancelHandler,
}: PaytmDefaultPayment) => {
  const isInline = shouldMountPaytmInline() && Boolean(document.getElementById(PAYTM_INLINE_CONTAINER_ID));
  if (isInline) forcePaytmDesktopWidth();
  const config: any = {
    root: isInline ? `#${PAYTM_INLINE_CONTAINER_ID}` : "",
    flow: "DEFAULT",
    data: {
      orderId: orderId,
      token: token,
      tokenType: "TXN_TOKEN",
      amount: amount,
    },
    handler: {
      notifyMerchant: function (eventName: any, data: any) {
        if (eventName === "APP_CLOSED") {
          resetPaytmDesktopWidth();
          onCancelHandler?.();
        }
      },
    },
  };
  if ((window as any).Paytm && (window as any).Paytm.CheckoutJS) {
    (window as any).Paytm.CheckoutJS.init(config)
      .then(function onSuccess() {
        (window as any).Paytm.CheckoutJS.invoke();
      })
      .catch(function onError(error: any) {
        console.log("Error => ", error);
        if (isInline) {
          // Don't leave an empty dialog (and the desktop width) behind when Paytm fails to start.
          resetPaytmDesktopWidth();
          onCancelHandler?.();
        }
      });
  }
};
